import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { IMAGES } from "@/lib/images";
import Reveal from "@/components/Reveal";
import DiamondMark from "@/components/DiamondMark";
import VentureCard from "@/components/public/VentureCard";

const MODEL = [
  { k: "Build", d: "Create and launch businesses with strong foundations." },
  { k: "Operate", d: "Run them with disciplined shared infrastructure." },
  { k: "Scale", d: "Grow with strategy, systems and technology." },
  { k: "Invest", d: "Reinvest into new and existing ventures." },
  { k: "Create Legacy", d: "Compound value for the long term." },
];

export default function Home() {
  const { settings } = useSite();
  const [ventures, setVentures] = useState([]);

  useEffect(() => {
    api.get("/ventures").then(({ data }) => setVentures(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden bg-bd-black grain">
        <img src={IMAGES.heroArchitecture} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-bd-black via-bd-black/70 to-bd-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="overline text-bd-gold mb-6"
          >
            A Diversified Business Group · Est. United Kingdom
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-bd-ivory text-4xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tight max-w-5xl"
          >
            {settings.hero_heading || "BUILDING BUSINESSES. CREATING LEGACY."}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-8 text-base md:text-lg text-bd-ivory/70 max-w-2xl leading-relaxed"
          >
            {settings.hero_sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <Link data-testid="hero-cta-ventures" to="/ventures" className="inline-flex items-center justify-center gap-2 bg-bd-gold text-bd-black hover:bg-bd-gold2 transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
              Explore Our Ventures <ArrowRight size={16} />
            </Link>
            <Link data-testid="hero-cta-partner" to="/partnerships" className="inline-flex items-center justify-center border border-bd-gold text-bd-gold hover:bg-bd-gold hover:text-bd-black transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
              Partner With Us
            </Link>
            <Link data-testid="hero-cta-opportunities" to="/acquisitions" className="inline-flex items-center justify-center border border-white/20 text-bd-ivory/80 hover:border-bd-ivory hover:text-bd-ivory transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
              Business Opportunities
            </Link>
          </motion.div>
        </div>
      </section>

      {/* GROUP INTRO */}
      <section className="bg-bd-ivory py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline text-bd-gold mb-6">The Group</p>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-bd-black max-w-2xl">
                One organisation. A family of businesses. A shared engine for growth.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base md:text-lg text-black/65 leading-relaxed max-w-xl">
                {settings.group_intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-black/70">
                {["Strategy", "Operations", "Marketing", "Technology", "Finance", "Growth"].map((c) => (
                  <span key={c} className="flex items-center gap-2"><span className="w-1 h-1 bg-bd-gold" />{c}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={IMAGES.officeInterior} alt="Black Diamond operations" className="w-full h-full object-cover" />
                <div className="absolute inset-0 border border-black/10" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GROUP MODEL */}
      <section className="bg-bd-black py-24 md:py-36 relative grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal>
            <p className="overline text-bd-gold mb-6">The Model</p>
            <h2 className="font-serif text-3xl md:text-5xl text-bd-ivory max-w-3xl leading-tight">
              How value compounds across the group
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {MODEL.map((m, i) => (
              <Reveal key={m.k} delay={i * 0.08}>
                <div className="bg-bd-black p-8 h-full min-h-[220px] flex flex-col justify-between group hover:bg-bd-charcoal transition-colors duration-500">
                  <span className="font-serif text-5xl text-bd-gold/30 group-hover:text-bd-gold/60 transition-colors">0{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-bd-ivory mb-2">{m.k}</h3>
                    <p className="text-sm text-bd-ivory/50 leading-relaxed">{m.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VENTURES PREVIEW */}
      <section className="bg-bd-charcoal py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="overline text-bd-gold mb-4">The Portfolio</p>
              <h2 className="font-serif text-3xl md:text-5xl text-bd-ivory max-w-xl leading-tight">
                A growing ecosystem of ventures
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/ventures" className="inline-flex items-center gap-2 text-bd-gold hover:text-bd-gold2 transition-colors text-sm tracking-[0.15em] uppercase whitespace-nowrap">
                All ventures <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ventures.map((v, i) => (
              <Reveal key={v.id} delay={i * 0.06}><VentureCard venture={v} index={i} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-bd-ivory py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <DiamondMark size={44} className="mx-auto mb-8" />
            <h2 className="font-serif text-3xl md:text-5xl text-bd-black leading-tight">
              Building for decades, not quarters.
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/60 max-w-2xl mx-auto">
              Whether you are a supplier, partner or hold an opportunity worth exploring, we would be glad to hear from you.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact" className="bg-bd-black text-bd-ivory hover:bg-bd-charcoal transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
                Contact the Group
              </Link>
              <Link to="/about" className="border border-black/20 text-bd-black hover:border-bd-gold hover:text-bd-gold transition-colors duration-300 px-8 py-4 text-sm tracking-[0.15em] uppercase">
                About Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
